import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useIntl, useModel, useSelector } from 'umi';
import debounce from 'lodash/debounce';
import isEmpty from 'lodash/isEmpty';

import { globalBranchesSearchRequest } from '@/services/api/location';
import { fetchUserBranches } from '@/services/api/user';
import {
  selectAllBranchesList,
  selectBranchDetails,
  selectBranchDetailLoading,
  selectFullBranchesLoading,
} from '@/selectors/locationSelectors';
import { LeadModel } from '@/typings/models/Lead';
import {
  addAnyAgentOption,
  branchHasServices,
  buildSearchResults,
  defaultAgentForService,
  Selection,
} from './utils';

type InitialValue = {
  serviceId?: number | string;
  agentId?: number | string;
  branchId?: number | string;
};

type Props = {
  value?: Selection;
  initialValue?: InitialValue;
  lead?: LeadModel;
  onChange: (value: Selection) => void;
};

export function useBranchCascader({ value, initialValue, lead, onChange }: Props) {
  const dispatch = useDispatch();
  const intl = useIntl();
  const { initialState } = useModel('@@initialState');
  const currentUserAlias = initialState?.currentUser?.User?.alias;
  const anyAgentLabel = intl.formatMessage({
    id: 'pages.calendar.anyAvailableAgent',
    defaultMessage: 'Any available agent',
  });

  const allBranches = useSelector(selectAllBranchesList);
  const branchDetails = useSelector(selectBranchDetails);
  const getBranchLoading = useSelector(selectBranchDetailLoading);
  const fullBranchesLoading = useSelector(selectFullBranchesLoading);
  const servicesLoading = getBranchLoading || fullBranchesLoading;

  const [selection, setSelection] = useState<Selection>({ branch: null, service: null, agent: null });
  const [searchType, setSearchTypeRaw] = useState<'Name' | 'Address'>('Name');
  const [searchTerm, setSearchTerm] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [addressBranches, setAddressBranches] = useState<any[] | null>(null);
  // Branches matched by the remote search. `null` = no active search (show everything).
  const [searchMatches, setSearchMatches] = useState<any[] | null>(null);
  // Users-only search → restricted per-branch tree (matched employees + their
  // services). `null` = show the selected branch's full services/agents.
  const [searchRestricted, setSearchRestricted] = useState<Record<string, any> | null>(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [addressLoading, setAddressLoading] = useState(false);

  const preselectedRef = useRef(false);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;
  const allBranchesRef = useRef(allBranches);
  allBranchesRef.current = allBranches;
  const anyAgentLabelRef = useRef(anyAgentLabel);
  anyAgentLabelRef.current = anyAgentLabel;
  const searchRestrictedRef = useRef(searchRestricted);
  searchRestrictedRef.current = searchRestricted;

  // Controlled/uncontrolled hybrid — local selection wins; falls back to form value
  const branch = selection.branch ?? value?.branch ?? null;
  const service = selection.service ?? value?.service ?? null;
  const agent = selection.agent ?? value?.agent ?? null;

  const isLocalMode = allBranches.length > 0 && allBranches.length <= 5;
  const isAddressSearch = searchType === 'Address';

  const commit = useCallback((next: Selection) => {
    setSelection(next);
    onChangeRef.current(next);
  }, []);
  const commitRef = useRef(commit);
  commitRef.current = commit;

  // "Me" is only auto-filled as the agent when nothing else asks for a specific
  // one: an explicit `initialValue.agentId` and the lead's assignee both win. The
  // drawer confirms every deviation from the lead's assignee, so silently picking
  // "me" over it would pop that confirmation as the drawer opens.
  const preferCurrentUser = !initialValue?.agentId && !lead?.assignee?.id;

  // Never prefers the current user inside a users-only search — that tree holds
  // exactly the employees the search matched, which is what the user asked to see.
  const pickDefaultAgent = useCallback(
    (svc: any, { restricted = false }: { restricted?: boolean } = {}) =>
      defaultAgentForService(svc, preferCurrentUser && !restricted ? currentUserAlias : null),
    [preferCurrentUser, currentUserAlias],
  );
  const pickDefaultAgentRef = useRef(pickDefaultAgent);
  pickDefaultAgentRef.current = pickDefaultAgent;

  const selectBranch = useCallback(
    (selectedBranch: any) => {
      const id = `${selectedBranch.id}`;
      // Users-only search: select from the restricted (matched-employees) tree.
      const restricted = searchRestrictedRef.current?.[id];
      if (restricted) {
        const firstService = restricted.services?.find((s: any) => s.agents?.length > 0) ?? null;
        commit({
          branch: restricted,
          service: firstService,
          agent: firstService?.agents?.[0] ?? null,
        });
        return;
      }
      const raw = branchDetails[id];
      if (!raw?.services) {
        dispatch({ type: 'location/getBranch', payload: { branchId: id, scoped: true } });
        commit({ branch: selectedBranch, service: null, agent: null });
        return;
      }
      // Data already cached — auto-select first service and agent immediately
      const processed = addAnyAgentOption(raw, anyAgentLabelRef.current);
      const firstService = processed.services?.find((s: any) => s.agents?.length > 0) ?? null;
      commit({
        branch: processed,
        service: firstService,
        agent: pickDefaultAgent(firstService),
      });
    },
    [branchDetails, dispatch, commit, pickDefaultAgent],
  );
  const selectBranchRef = useRef(selectBranch);
  selectBranchRef.current = selectBranch;

  const selectService = useCallback(
    (selectedService: any) => {
      const agent = pickDefaultAgent(selectedService, {
        restricted: !!searchRestrictedRef.current?.[`${branch?.id}`],
      });
      commit({ branch, service: selectedService, agent });
    },
    [branch, commit, pickDefaultAgent],
  );

  const selectAgent = useCallback(
    (selectedAgent: any) => {
      commit({ branch, service, agent: selectedAgent });
      setDropdownOpen(false);
    },
    [branch, service, commit],
  );

  const clear = useCallback(() => {
    commit({ branch: null, service: null, agent: null, clear: true });
  }, [commit]);

  const setSearchType = useCallback((type: 'Name' | 'Address') => {
    setSearchTypeRaw(type);
    setSearchTerm('');
    setSearchMatches(null);
    setSearchRestricted(null);
    setAddressBranches(null);
  }, []);

  const baseBranches = isAddressSearch ? (addressBranches ?? []) : allBranches;

  // Search only narrows down WHICH branches appear — it never filters the
  // services/agents inside a branch.
  const filteredBranches = useMemo(() => {
    // Remote search: show exactly the matched branches (full objects), nothing more.
    if (searchMatches) return searchMatches;
    if (isAddressSearch || !searchTerm.trim() || !isLocalMode) return baseBranches;

    // Local search (≤5 branches, details preloaded): a branch matches by its own
    // name, by one of its service names, or by one of its employees' names.
    const term = searchTerm.trim().toLowerCase();
    return baseBranches.filter(b => {
      if (b.name?.toLowerCase().includes(term)) return true;
      const full = branchDetails[`${b.id}`];
      return (full?.services || []).some(
        (s: any) =>
          s.service_type?.name?.toLowerCase().includes(term) ||
          (s.agents || []).some((a: any) =>
            `${a.firstname ?? ''} ${a.lastname ?? ''}`.toLowerCase().includes(term),
          ),
      );
    });
  }, [baseBranches, searchMatches, isAddressSearch, searchTerm, isLocalMode, branchDetails]);

  // A name search is active while the user has typed a term (remote or local).
  const nameSearchActive = !isAddressSearch && !!searchTerm.trim();

  // Services/agents shown for the selected branch:
  //  - users-only search → the restricted tree (just the matched employees);
  //  - otherwise → the branch's FULL services/agents from branch details.
  // When a name search is active and the selected branch isn't part of the matched
  // set, show nothing — this keeps the branch, service and agent columns consistent
  // (no stale "services filled, branch empty").
  const activeBranchData = useMemo(() => {
    if (!branch?.id) return null;
    const id = `${branch.id}`;
    if (nameSearchActive && !filteredBranches.some((b: any) => `${b.id}` === id)) return null;
    if (searchRestricted) return searchRestricted[id] ?? null;
    const raw = branchDetails[id] ?? null;
    return raw ? addAnyAgentOption(raw, anyAgentLabel) : null;
  }, [branch?.id, nameSearchActive, filteredBranches, searchRestricted, branchDetails, anyAgentLabel]);

  const sortedBranches = useMemo(() => {
    const withServices = filteredBranches.filter(b => {
      // Hide branches the API reports as having no services — except in a
      // users-only search, where a matched employee's branch is authoritative
      // (it provably has services) even if a stale count says otherwise.
      if (!searchRestricted && !branchHasServices(b)) return false;
      const full = branchDetails[`${b.id}`];
      if (!full?.services) return true;
      // A service with no named agents is still bookable via "any available
      // agent", so require services to exist — not agents.
      return full.services.length > 0;
    });
    if (!isAddressSearch) return withServices;
    return [...withServices].sort((a, b) => (a.distance || 0) - (b.distance || 0));
  }, [filteredBranches, branchDetails, isAddressSearch, searchRestricted]);

  const visibleServices = useMemo(
    () => (activeBranchData?.services || []).filter((s: any) => s.agents?.length > 0),
    [activeBranchData],
  );

  const visibleAgents = useMemo(
    () => activeBranchData?.services?.find((s: any) => s.id === service?.id)?.agents ?? [],
    [activeBranchData, service?.id],
  );

  // Initial data fetch
  useEffect(() => {
    if (isEmpty(allBranches)) {
      dispatch({ type: 'location/getAllBranchList' });
    }
    const branchId = initialValue?.branchId ?? lead?.branch?.id;
    if (branchId && !branchDetails[`${branchId}`]?.services) {
      dispatch({ type: 'location/getBranch', payload: { branchId: `${branchId}`, scoped: true } });
    }
  }, []); // eslint-disable-line

  // Preselection — waits for branch data to land in branchDetails
  useEffect(() => {
    if (preselectedRef.current) return;

    if (initialValue?.serviceId) {
      const branchId = initialValue?.branchId;
      if (!branchId) {
        preselectedRef.current = true;
        commitRef.current({ branch: null, service: null, agent: null, clear: true });
        return;
      }
      const raw = branchDetails[`${branchId}`];
      if (!raw?.services) return;
      const processed = addAnyAgentOption(raw, anyAgentLabelRef.current);
      const svc =
        processed.services?.find((s: any) => `${s.id}` === `${initialValue.serviceId}`) ??
        processed.services?.[0] ??
        null;
      const ag = svc
        ? initialValue.agentId
          ? svc.agents?.find((a: any) => `${a.id}` === `${initialValue.agentId}`) ?? svc.agents?.[0]
          : pickDefaultAgentRef.current(svc)
        : null;
      preselectedRef.current = true;
      commitRef.current({ branch: processed, service: svc, agent: ag ?? null });
      return;
    }

    if (!lead) {
      preselectedRef.current = true;
      commitRef.current({ branch: null, service: null, agent: null, clear: true });
      return;
    }

    const branchId = lead?.branch?.id;
    if (!branchId) {
      preselectedRef.current = true;
      return;
    }

    const raw = branchDetails[`${branchId}`];
    if (!raw?.services) return;

    const processed = addAnyAgentOption(raw, anyAgentLabelRef.current);
    const assigneeId = lead?.assignee?.id;

    if (assigneeId) {
      for (const svc of processed.services) {
        for (const ag of svc.agents) {
          if (ag.id === assigneeId) {
            preselectedRef.current = true;
            commitRef.current({ branch: processed, service: svc, agent: ag });
            return;
          }
        }
      }
    }

    const svc = processed.services?.[0] ?? null;
    preselectedRef.current = true;
    commitRef.current({ branch: processed, service: svc, agent: pickDefaultAgentRef.current(svc) });
  }, [branchDetails]); // eslint-disable-line

  // Auto-select first service when branch data arrives (after a branch is picked,
  // whether by click or as the first result of a search)
  useEffect(() => {
    if (!branch?.id || service) return;
    const id = `${branch.id}`;
    // In a users-only search the restricted tree owns the selection — don't fill
    // from full details here.
    if (searchRestrictedRef.current?.[id]) return;
    const raw = branchDetails[id];
    if (!raw?.services?.length) return;
    const processed = addAnyAgentOption(raw, anyAgentLabelRef.current);
    const firstService = processed.services.find((s: any) => s.agents?.length > 0) ?? null;
    if (!firstService) return;
    commitRef.current({
      branch: processed,
      service: firstService,
      agent: pickDefaultAgentRef.current(firstService),
    });
  }, [branchDetails]); // eslint-disable-line

  // Local search auto-select first result
  useEffect(() => {
    if (!isLocalMode || isAddressSearch || !searchTerm.trim()) return;
    const first = filteredBranches[0];
    if (!first) return;
    const raw = branchDetails[`${first.id}`];
    if (!raw?.services?.length) return;
    const processed = addAnyAgentOption(raw, anyAgentLabelRef.current);
    const svc = processed.services?.[0] ?? null;
    if (svc) commitRef.current({ branch: processed, service: svc, agent: pickDefaultAgentRef.current(svc) });
  }, [searchTerm]); // eslint-disable-line

  // Remote text search (debounced). A search must NEVER show a flash/toast — it
  // only narrows the branch column. We read the response directly (instead of
  // processAPIResponseNow, whose error path would surface the backend's message
  // with an error icon) and swallow any failure into an empty result set. The
  // first match is auto-selected so its full services/agents load right away.
  const searchRemotely = useMemo(
    () =>
      debounce((term: string) => {
        if (!term.trim()) {
          searchRestrictedRef.current = null;
          setSearchRestricted(null);
          setSearchMatches(null);
          return;
        }
        setSearchLoading(true);
        globalBranchesSearchRequest({ query: { search: term } })
          .then((res: any) => {
            let matches: any[] = [];
            let restricted: Record<string, any> | null = null;
            try {
              const result = res?.data?.data;
              if (result) ({ matchedBranches: matches, restricted } = buildSearchResults(result, allBranchesRef.current));
            } catch {
              matches = [];
              restricted = null;
            }
            // Outside a users-only search, drop branches the API reports as having
            // no services so the branch column, the bug-3 gate and the auto-selected
            // first match all agree on the same set.
            if (!restricted) matches = matches.filter(branchHasServices);
            // Update the ref synchronously so the auto-select below reads it.
            searchRestrictedRef.current = restricted;
            setSearchRestricted(restricted);
            setSearchMatches(matches);
            const first = matches[0];
            if (first) selectBranchRef.current(first);
          })
          .catch(() => {
            searchRestrictedRef.current = null;
            setSearchRestricted(null);
            setSearchMatches([]);
          })
          .finally(() => setSearchLoading(false));
      }, 500),
    [], // eslint-disable-line
  );

  useEffect(() => {
    if (isAddressSearch) return;
    if (!searchTerm.trim()) {
      setSearchMatches(null);
      setSearchRestricted(null);
      return;
    }
    if (!isLocalMode) searchRemotely(searchTerm);
  }, [searchTerm]); // eslint-disable-line

  useEffect(() => () => searchRemotely.cancel(), [searchRemotely]);

  const onAddressSearch = useCallback(
    (place: { lat: number; lng: number }) => {
      setAddressLoading(true);
      fetchUserBranches({ query: { lat: place.lat, lng: place.lng, full: 1, in_scope: 1 } })
        .then(response => {
          const data: any[] = response?.data?.data || [];
          const sorted = [...data].sort((a, b) => (a.distance || 0) - (b.distance || 0));
          sorted.forEach(b =>
            dispatch({
              type: 'location/setBranchDetail',
              payload: { branchId: b.id, branchData: b },
            }),
          );
          setAddressBranches(sorted);
          setSearchTerm('');
          setSearchMatches(null);
          setSearchRestricted(null);
        })
        .finally(() => setAddressLoading(false));
    },
    [dispatch],
  );

  return {
    branch,
    service,
    agent,
    sortedBranches,
    visibleServices,
    visibleAgents,
    searchType,
    setSearchType,
    searchTerm,
    setSearchTerm,
    dropdownOpen,
    setDropdownOpen,
    isAddressSearch,
    searchLoading,
    addressLoading,
    servicesLoading,
    selectBranch,
    selectService,
    selectAgent,
    clear,
    onAddressSearch,
  };
}
